import { useRef, useState } from 'react'
import Section from '../ui/Section/Section'
import Reveal from '../ui/Reveal/Reveal'
import Tag from '../ui/Tag/Tag'
import { ExternalLinkIcon } from '../ui/Icon/Icon'
import { experience } from '../../data/portfolio'
import styles from './Experience.module.css'

const Experience = ({ index }) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const tabRefs = useRef([])

  if (!experience.length) return null

  const selectTab = (next) => {
    const wrapped = (next + experience.length) % experience.length
    setActiveIndex(wrapped)
    tabRefs.current[wrapped]?.focus()
  }

  const handleKeyDown = (event) => {
    const moves = {
      ArrowDown: activeIndex + 1,
      ArrowRight: activeIndex + 1,
      ArrowUp: activeIndex - 1,
      ArrowLeft: activeIndex - 1,
      Home: 0,
      End: experience.length - 1,
    }

    if (!(event.key in moves)) return

    event.preventDefault()
    selectTab(moves[event.key])
  }

  return (
    <Section
      id="experience"
      index={index}
      title="Experience"
      eyebrow="Experience"
      lead="Where I've worked and what I owned."
    >
      <Reveal className={styles.layout}>
        <div
          role="tablist"
          aria-label="Companies"
          aria-orientation="vertical"
          className={styles.tabs}
          onKeyDown={handleKeyDown}
          style={{ '--active-index': activeIndex }}
        >
          {experience.map((role, position) => {
            const isActive = position === activeIndex

            return (
              <button
                key={role.id}
                ref={(element) => {
                  tabRefs.current[position] = element
                }}
                type="button"
                role="tab"
                id={`experience-tab-${role.id}`}
                aria-controls={`experience-panel-${role.id}`}
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                className={`${styles.tab} ${isActive ? styles.tabActive : ''}`.trim()}
                onClick={() => setActiveIndex(position)}
              >
                {role.shortName || role.company}
              </button>
            )
          })}
        </div>

        {experience.map((role, position) => (
          <div
            key={role.id}
            role="tabpanel"
            id={`experience-panel-${role.id}`}
            aria-labelledby={`experience-tab-${role.id}`}
            hidden={position !== activeIndex}
            tabIndex={0}
            className={styles.panel}
          >
            <h3 className={styles.role}>
              {role.role}
              <span className={styles.at}> · </span>
              {role.url ? (
                <a
                  href={role.url}
                  className={styles.company}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {role.company}
                  <ExternalLinkIcon size={14} />
                </a>
              ) : (
                <span className={styles.company}>{role.company}</span>
              )}
            </h3>

            <p className={styles.period}>{role.period}</p>

            {role.description && <p className={styles.description}>{role.description}</p>}

            {role.stack?.length > 0 && (
              <ul className={styles.stack}>
                {role.stack.map((tech) => (
                  <Tag key={tech}>{tech}</Tag>
                ))}
              </ul>
            )}
          </div>
        ))}
      </Reveal>
    </Section>
  )
}

export default Experience
